import { ApiResponseError, call, portalApi } from '../api/client'
import { useTenant } from '../tenant/TenantContext'
import { normalizeBranding } from '../theme/branding'
import type { PortalBranding } from '../theme/branding'
import {
  clearCachedBranding,
  readCachedBranding,
  writeCachedBranding,
} from '../theme/brandingCache'
import { useQuery } from '@tanstack/react-query'

export type { PortalBranding }

const BEACON_ID_OVERRIDE_KEY = 'hsPortalBeaconIdOverride'

// Dev-only escape hatch. Local/stack environments only list local Beacons, whose
// ids 404 on the production Beacon CDN, so the Beacon flow can't be exercised
// there through the UI. On dev hosts, localStorage['hsPortalBeaconIdOverride']
// stands in for the configured beaconId (set it from the console, then reload).
// Never honored on production portal hosts.
const isDevHost = (): boolean => {
  const { hostname } = window.location
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.hsenv.io')
  )
}

const applyBeaconIdOverride = (beaconId: string | null): string | null => {
  if (!isDevHost()) return beaconId
  const override = window.localStorage.getItem(BEACON_ID_OVERRIDE_KEY)?.trim()
  return override ? override : beaconId
}

export type PortalConfigState =
  | { kind: 'loading' }
  | { kind: 'available'; branding: PortalBranding; beaconId?: string | null }
  | { kind: 'unavailable'; reason: 'not-onboarded' | 'outage' }

interface PortalConfigData {
  branding: PortalBranding
  beaconId: string | null
}

const fetchPortalConfig = async (
  slug: string,
  signal?: AbortSignal
): Promise<PortalConfigData> => {
  try {
    const [brand, portal] = await Promise.all([
      call(() => portalApi.getPortalBrand({ slug }, { signal })),
      call(() => portalApi.getPortalDescriptor({ slug }, { signal })),
    ])
    const branding = normalizeBranding(brand)
    // A disabled portal isn't available to end users; treat it like a missing
    // one so the cache is cleared and the unavailable screen renders, rather
    // than painting a sign-in page that auth would reject server-side.
    if (portal.enabled === false) throw new ApiResponseError('not-found')
    writeCachedBranding(slug, branding)
    return { branding, beaconId: portal.beaconId ?? null }
  } catch (error) {
    // A de-onboarded portal should stop serving stale cached branding.
    if (error instanceof ApiResponseError && error.kind === 'not-found') {
      clearCachedBranding(slug)
    }
    throw error
  }
}

/**
 * Fetches portal branding for App bootstrap. Seeds from the localStorage cache
 * for an instant in-brand paint, revalidates on mount, and falls back to the
 * last-cached branding on transient errors — hard-failing only on not-found.
 * beaconId is not cached; it stays null until the live fetch resolves.
 */
export const usePortalConfig = (): PortalConfigState => {
  const slug = useTenant()
  const query = useQuery({
    queryKey: ['branding', slug],
    enabled: Boolean(slug),
    queryFn: ({ signal }) => fetchPortalConfig(slug as string, signal),
    initialData: slug
      ? () => {
          const cached = readCachedBranding(slug)
          return cached ? { branding: cached, beaconId: null } : undefined
        }
      : undefined,
    staleTime: 0,
  })

  if (!slug) return { kind: 'unavailable', reason: 'not-onboarded' }

  if (
    query.error instanceof ApiResponseError &&
    query.error.kind === 'not-found'
  ) {
    return { kind: 'unavailable', reason: 'not-onboarded' }
  }
  if (query.data)
    return {
      kind: 'available',
      branding: query.data.branding,
      beaconId: applyBeaconIdOverride(query.data.beaconId),
    }
  if (query.error) return { kind: 'unavailable', reason: 'outage' }
  return { kind: 'loading' }
}
